import { useEffect } from 'react';

/**
 * Scroll-driven 3D. Attributes go on any element:
 *   data-rise="1" data-yaw="-1"  stands up out of depth as it enters
 *   data-depth="1.05"            parallax plane (Y drift + constant Z)
 *   data-flat="-150"             pin the Z instead of deriving it from depth
 *   data-tilt="1"                rotateX with scroll position
 *   data-fade="1"                fade with distance from viewport centre
 * The parent needs `perspective`. Flattens below 820px and when k is 0.
 */
export default function useScroll3D(deps = [], k = 1) {
  useEffect(() => {
    let nodes = Array.from(document.querySelectorAll('[data-depth],[data-rise]'));
    let raf = 0;

    const apply = () => {
      raf = 0;
      const vh = window.innerHeight || 800;
      const flat = window.innerWidth < 820 || !k;
      nodes.forEach((el) => {
        if (flat) { el.style.transform = ''; el.style.opacity = ''; return; }
        const r = el.getBoundingClientRect();
        const d = parseFloat(el.dataset.depth) || 0;
        const rise = parseFloat(el.dataset.rise) || 0;
        const yaw = parseFloat(el.dataset.yaw) || 0;
        const tilt = parseFloat(el.dataset.tilt) || 0;
        let t = '';
        if (rise || yaw) {
          const p = Math.min(1, Math.max(0, (vh * 0.9 - r.top) / (vh * 0.52)));
          const e = 1 - Math.pow(1 - p, 3);
          const g = (1 - e) * k;
          el.style.transformOrigin = '50% 100%';
          t = 'perspective(1100px) translateY(' + (g * 90).toFixed(1) +
              'px) translateZ(' + (g * -420 * (rise || 1)).toFixed(1) +
              'px) rotateX(' + (g * 26 * rise).toFixed(2) +
              'deg) rotateY(' + (g * 20 * yaw).toFixed(2) + 'deg)';
          el.style.opacity = String(Math.min(1, 0.18 + e * 1.15));
        }
        if (d || tilt) {
          const c = Math.max(-1.3, Math.min(1.3, (r.top + r.height / 2 - vh / 2) / vh));
          const dz = el.dataset.flat ? parseFloat(el.dataset.flat) * k : -d * 480 * k;
          t += ' translate3d(0,' + (-c * d * 150 * k).toFixed(1) + 'px,' + dz.toFixed(1) +
               'px) rotateX(' + (c * tilt * 9 * k).toFixed(2) + 'deg)';
          if (el.dataset.fade) el.style.opacity = String(Math.max(0.28, 1 - Math.abs(c) * 0.7));
        }
        el.style.willChange = 'transform';
        el.style.transform = t;
      });
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };

    nodes = Array.from(document.querySelectorAll('[data-depth],[data-rise]'));
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
